import React from "react";
import { ChevronRight, Language } from "@mui/icons-material";
import { alpha, Box, Paper, Typography } from "@mui/material";
import { SiteListApiItem } from "../../../site-listings/services/siteListingConfig";
import { SiteSeoStats } from "../../hooks/useSiteSeoStats";

interface WebsiteCardProps {
  site: SiteListApiItem;
  stats?: SiteSeoStats;
  onClick: () => void;
}

const scoreColor = (score: number): "success" | "warning" | "error" =>
  score >= 80 ? "success" : score >= 50 ? "warning" : "error";

const WebsiteCard: React.FC<WebsiteCardProps> = ({ site, stats, onClick }) => {
  const score = stats?.score ?? 0;
  const color = scoreColor(score);

  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: 3,
        cursor: "pointer",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
        "&:hover": { boxShadow: 2, borderColor: "primary.main" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: "action.hover",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Language color="action" />
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography fontWeight={800} noWrap>{site.name || `Website ${site.id}`}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>{site.domain || "Not set"}</Typography>
        </Box>
        <ChevronRight color="disabled" />
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ display: "flex", gap: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Pages</Typography>
            <Typography fontWeight={700}>{stats?.pageCount ?? "—"}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Last Updated</Typography>
            <Typography fontWeight={700}>
              {site.updated_at ? new Date(site.updated_at).toLocaleDateString("en-CA") : "—"}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            px: 2,
            py: 0.75,
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette[color].main, 0.14),
            color: (theme) => theme.palette[color].dark,
          }}
        >
          <Typography fontWeight={800} fontSize="1.1rem">{score}</Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default WebsiteCard;
